import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LoadableData } from 'types';
import {
  DictionaryWord,
  VocabularyIrregularVerb,
  ValueOf,
  VocabularyTypeRecord,
  SubCategoryTO,
  WordSetCategoryType,
  WordSetDataRecord,
  DictionaryIrregularVerb,
  WordSetType,
  PageCategoryTO,
  CategoryTO,
  VocabularyWordType,
} from 'app/types';

export interface VocabularyWordsFilters {
  isActiveSpeech?: string;
  isLearned?: string;
  partOfSpeech?: string;
}

export interface VocabularyWordsGetPayload {
  append?: boolean;
  filters?: VocabularyWordsFilters;
  page?: number;
  size?: number;
  wordType?: VocabularyWordType;
  /**
   * BE-side sort key. `'recent'` → addedAt DESC (id tiebreaker), required
   * for the "Нещодавні" grouping to actually surface recent batches
   * regardless of which alphabetical page they fall on. Omit for the
   * default word-ASC order.
   */
  sortBy?: 'recent' | 'word';
  /**
   * Case-insensitive substring filter on word / translation / definition.
   * Applied BE-side so the search hits the whole vocabulary, not just the
   * current page. Blank / undefined disables the filter.
   */
  search?: string;
}

interface VocabularyWordsState extends LoadableData<Array<DictionaryWord>> {
  number: number;
  partOfSpeechOptions: string[];
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface Slice {
  wordSetData: LoadableData<Array<WordSetDataRecord>>;
  wordSetCategories: Record<WordSetCategoryType, LoadableData<PageCategoryTO>>;
  categories: Record<string, LoadableData<Array<SubCategoryTO>>>;
  subCategoryWords: LoadableData<Array<DictionaryWord>>;
  irregularVerbWordsByLevel: Record<
    string,
    LoadableData<Array<DictionaryIrregularVerb>>
  >;
  vocabulary: LoadableData<Array<VocabularyTypeRecord>>;
  vocabularyWords: VocabularyWordsState;
  vocabularyExpressions: VocabularyWordsState;
  vocabularyVerbs: LoadableData<Array<VocabularyIrregularVerb>>;
}

export interface CommonSlice extends Slice {}

const getInitialState = (): Slice => ({
  wordSetData: { data: [], loading: true },
  wordSetCategories: {
    'level-based': {
      data: { title: '', subTitle: '', locale: '', categories: [] },
      initiated: false,
      loading: true,
    },
    'most-common': {
      data: { title: '', subTitle: '', locale: '', categories: [] },
      initiated: false,
      loading: true,
    },
    'topic-related': {
      data: { title: '', subTitle: '', locale: '', categories: [] },
      initiated: false,
      loading: true,
    },
    collocations: {
      data: { title: '', subTitle: '', locale: '', categories: [] },
      initiated: false,
      loading: true,
    },
    idioms: {
      data: { title: '', subTitle: '', locale: '', categories: [] },
      initiated: false,
      loading: true,
    },
    'phrasal-verbs': {
      data: { title: '', subTitle: '', locale: '', categories: [] },
      initiated: false,
      loading: true,
    },
    'irregular-verb': {
      data: { title: '', subTitle: '', locale: '', categories: [] },
      initiated: false,
      loading: true,
    },
  },
  categories: {},
  subCategoryWords: { data: [], loading: true },
  vocabulary: { data: [], loading: true },
  vocabularyWords: {
    data: [],
    loading: true,
    number: 0,
    partOfSpeechOptions: [],
    size: 20,
    totalElements: 0,
    totalPages: 0,
  },
  vocabularyExpressions: {
    data: [],
    loading: true,
    number: 0,
    partOfSpeechOptions: [],
    size: 20,
    totalElements: 0,
    totalPages: 0,
  },
  vocabularyVerbs: { data: [], loading: true },
  irregularVerbWordsByLevel: {},
});

const getVocabularyWordsStateKey = (wordType?: VocabularyWordType) =>
  wordType === 'EXPRESSION' ? 'vocabularyExpressions' : 'vocabularyWords';

const commonSlice = createSlice({
  name: 'commonSlice',
  initialState: getInitialState(),
  reducers: {
    getCommonData: () => {},
    getVocabularyData: () => {},
    vocabularyWordsGet: (
      store,
      action: PayloadAction<VocabularyWordsGetPayload | undefined>,
    ) => {
      const stateKey = getVocabularyWordsStateKey(action.payload?.wordType);
      store[stateKey].loading = true;
      if (!action.payload?.append) {
        store[stateKey].data = [];
      }
    },
    vocabularyWordsDelete: (
      store,
      {
        payload,
      }: PayloadAction<{
        filters?: VocabularyWordsFilters;
        page?: number;
        size?: number;
        wordType?: VocabularyWordType;
        wordIds: number[];
      }>,
    ) => {
      const stateKey = getVocabularyWordsStateKey(payload.wordType);
      store[stateKey].loading = true;
    },
    vocabularyWordsUpdate: (
      store,
      {
        payload,
      }: PayloadAction<{
        filters?: VocabularyWordsFilters;
        page?: number;
        size?: number;
        wordType?: VocabularyWordType;
        words: DictionaryWord[];
      }>,
    ) => {
      const stateKey = getVocabularyWordsStateKey(payload.wordType);
      store[stateKey].loading = true;
    },
    vocabularyWordsSet: (
      store,
      {
        payload,
      }: PayloadAction<{
        append?: boolean;
        content: Slice['vocabularyWords']['data'];
        number: number;
        partOfSpeechOptions: string[];
        size: number;
        totalElements: number;
        totalPages: number;
        wordType?: VocabularyWordType;
      }>,
    ) => {
      const stateKey = getVocabularyWordsStateKey(payload.wordType);
      store[stateKey].loading = false;
      store[stateKey].data = payload.append
        ? [...store[stateKey].data, ...payload.content]
        : payload.content;
      store[stateKey].number = payload.number;
      store[stateKey].partOfSpeechOptions = payload.partOfSpeechOptions;
      store[stateKey].size = payload.size;
      store[stateKey].totalElements = payload.totalElements;
      store[stateKey].totalPages = payload.totalPages;
    },
    vocabularyVerbsGet: (store) => {
      store.vocabularyVerbs.loading = true;
    },
    vocabularyVerbsDelete: (
      store,
      { payload: _payload }: PayloadAction<{ verbIds: number[] }>,
    ) => {
      store.vocabularyVerbs.loading = true;
    },
    vocabularyVerbsUpdate: (
      store,
      { payload: _payload }: PayloadAction<{ verbs: VocabularyIrregularVerb[] }>,
    ) => {
      store.vocabularyVerbs.loading = true;
    },
    vocabularyVerbsSet: (
      store,
      { payload }: PayloadAction<Slice['vocabularyVerbs']['data']>,
    ) => {
      store.vocabularyVerbs.loading = false;
      store.vocabularyVerbs.data = payload;
    },
    setVocabularyData: (
      state,
      { payload }: PayloadAction<Slice['vocabulary']['data']>,
    ) => {
      state.vocabulary.loading = false;
      state.vocabulary.data = payload;
    },
    setWordSetData: (
      state,
      { payload }: PayloadAction<Slice['wordSetData']['data']>,
    ) => {
      state.wordSetData.data = payload;
      state.wordSetData.loading = false;
    },
    setWordSetCategories: (
      store,
      {
        payload: { wordSetType, data },
      }: PayloadAction<{
        wordSetType: WordSetCategoryType;
        data: PageCategoryTO;
      }>,
    ) => {
      store.wordSetCategories[wordSetType].loading = false;
      store.wordSetCategories[wordSetType].data = data; // Записуємо дані
    },
    getIrregularVerbWords: (
      state,
      { payload: { levelBased } }: PayloadAction<{ levelBased: string }>,
    ) => {
      state.irregularVerbWordsByLevel[levelBased] = {
        data: state.irregularVerbWordsByLevel[levelBased]?.data || [],
        loading: true,
      };
    },
    setIrregularVerbWords: (
      state,
      {
        payload: { levelBased, data },
      }: PayloadAction<{
        levelBased: string;
        data: Array<DictionaryIrregularVerb>;
      }>,
    ) => {
      state.irregularVerbWordsByLevel[levelBased] = {
        data,
        loading: false,
      };
    },
    getCategories: (
      state,
      {
        payload: { wordSetType: _wordSetType, categoryName },
      }: PayloadAction<{ categoryName: string; wordSetType: string }>,
    ) => {
      if (!state.categories[categoryName]) {
        state.categories[categoryName] = {
          data: [],
          loading: true,
        };
      }
    },
    getSubCategoryWords: (
      state,
      _action: PayloadAction<{
        categoryName: string;
        subcategoryName: string;
        wordSetType: string;
      }>,
    ) => {
      state.subCategoryWords.loading = true;
    },
    setSubCategoryWords: (
      state,
      { payload }: PayloadAction<Slice['subCategoryWords']['data']>,
    ) => {
      state.subCategoryWords.loading = false;
      state.subCategoryWords.data = payload;
    },
    setCategories: (
      state,
      {
        payload: { categoryName, wordSetType: _wordSetType, data },
      }: PayloadAction<{
        categoryName: string;
        wordSetType: string;
        data: ValueOf<Slice['categories']>['data'];
      }>,
    ) => {
      if (state.categories[categoryName]) {
        state.categories[categoryName].loading = false;
        state.categories[categoryName].data = data;
      }
    },
  },
  selectors: {
    getWordSetDataById: (store, id: WordSetType) => {
      return store.wordSetData.data.find(
        ({ wordSetType }) => id === wordSetType,
      );
    },

    getWordSetCategory: (
      store,
      wordSetType: WordSetCategoryType,
    ): LoadableData<PageCategoryTO> => {
      return store.wordSetCategories[wordSetType];
    },

    getCategoryData: (
      store,
      wordSetType: WordSetCategoryType,
      categoryName: string,
    ): CategoryTO | undefined => {
      const wordSetCategory = commonSlice
        .getSelectors()
        .getWordSetCategory(store, wordSetType);

      if (
        wordSetCategory &&
        wordSetCategory.data &&
        'categories' in wordSetCategory.data
      ) {
        return wordSetCategory.data.categories.find(
          (category) => category.categoryName === categoryName,
        );
      }

      return undefined; // Якщо структура неочікувана
    },
    getSubcategoryData: (
      store,
      categoryName: string,
    ): ValueOf<typeof store.categories> | undefined =>
      store.categories[categoryName],
  },
});

export default commonSlice;
export const commonActions = commonSlice.actions;
export const commonSelectors = commonSlice.selectors;
